/* ------------------------------------------------------------
*** Scrolling content tiles — config (timings, scroll length, z-index)
------------------------------------------------------------ */

const SCROLLING_TILES = Object.freeze({
  DEFAULT_DESKTOP_MIN_WIDTH_PX: 990,
  HEADER_OFFSET_FALLBACK_PX: 69, // Matches `--scrolling-tiles-header-offset` from the section when unreadable.
  SCROLL_PER_SLIDE_VH: 2,
  SCROLL_EXTRA_AFTER_LAST_VH: 0.85,
  TRANSITION_FADE_OUT_S: 0.3,
  TRANSITION_FADE_OUT_DELAY_S: 0,
  TRANSITION_SLIDE_S: 0.6,
  CONTENT_FADE_UP_IN_S: 0.6,
  CONTENT_FADE_DELAY_S: 0.05,
  CONTENT_ENTER_Y_PERCENT: 150,
  CONTENT_STAGGER_S: 0.05,
  EASE_CONTENT_OUT: "power1.in",
  EASE_CONTENT_UP_IN: "power3.out",
  EASE_SLIDE: "power2.out",
  MEDIA_Z_INCOMING: 50,
  MEDIA_Z_OUTGOING: 1,
  MEDIA_Z_SETTLE_BEFORE: 0,
  MEDIA_Z_SETTLE_ACTIVE: 2,
});

/* ------------------------------------------------------------
*** Media queries — window.themeScrollConfig (lenis-smooth-scroll.js)
------------------------------------------------------------ */

const resolveScrollingTilesDesktopMediaQuery = () => {
  const mq = window.themeScrollConfig?.desktopMediaQuery;

  if (typeof mq === "string" && mq.length > 0) {
    return mq;
  }

  return `(min-width: ${SCROLLING_TILES.DEFAULT_DESKTOP_MIN_WIDTH_PX}px)`;
};

const resolveReducedMotionMediaQuery = () => {
  const mq = window.themeScrollConfig?.reducedMotionMediaQuery;

  if (typeof mq === "string" && mq.length > 0) {
    return mq;
  }

  return "(prefers-reduced-motion: reduce)";
};

const scrollingTilesGsapReady = () =>
  typeof gsap !== "undefined" && typeof ScrollTrigger !== "undefined";

/* ------------------------------------------------------------
*** Desktop resize — one rAF-coalesced ScrollTrigger.refresh (990+)
------------------------------------------------------------ */

let scrollingTilesResizeRafId = null;
let scrollingTilesResizeListenerCount = 0;
let scrollingTilesBoundWindowResize = null;

const scrollingTilesScheduleScrollTriggerRefresh = () => {
  if (scrollingTilesResizeRafId !== null) {
    return;
  }

  scrollingTilesResizeRafId = window.requestAnimationFrame(() => {
    scrollingTilesResizeRafId = null;

    if (!scrollingTilesGsapReady()) {
      return;
    }

    if (!window.matchMedia(resolveScrollingTilesDesktopMediaQuery()).matches) {
      return;
    }

    ScrollTrigger.refresh();
  });
};

const scrollingTilesHandleWindowResize = () => {
  scrollingTilesScheduleScrollTriggerRefresh();
};

const scrollingTilesAttachWindowResize = () => {
  if (scrollingTilesResizeListenerCount === 0) {
    scrollingTilesBoundWindowResize = scrollingTilesHandleWindowResize;
    window.addEventListener("resize", scrollingTilesBoundWindowResize, {
      passive: true,
    });
    window.addEventListener(
      "orientationchange",
      scrollingTilesBoundWindowResize,
    );
  }

  scrollingTilesResizeListenerCount += 1;
};

const scrollingTilesDetachWindowResize = () => {
  scrollingTilesResizeListenerCount -= 1;

  if (scrollingTilesResizeListenerCount > 0) {
    return;
  }

  scrollingTilesResizeListenerCount = 0;

  if (scrollingTilesBoundWindowResize) {
    window.removeEventListener("resize", scrollingTilesBoundWindowResize);
    window.removeEventListener(
      "orientationchange",
      scrollingTilesBoundWindowResize,
    );
    scrollingTilesBoundWindowResize = null;
  }

  if (scrollingTilesResizeRafId !== null) {
    window.cancelAnimationFrame(scrollingTilesResizeRafId);
    scrollingTilesResizeRafId = null;
  }
};

const getScrollingSlideRoots = (host) => ({
  mediaPanels: host.querySelectorAll("[data-scrolling-tiles-media-panel]"),
  contentPanels: host.querySelectorAll("[data-scrolling-tiles-content-panel]"),
  paginationRoot: host.querySelector("[data-scrolling-tiles-pagination]"),
});

const scrollDocumentToY = (top) => {
  const themeLenis = window.themeLenis;

  if (themeLenis && typeof themeLenis.scrollToY === "function") {
    themeLenis.scrollToY(top);
    return;
  }

  const desktopMq = resolveScrollingTilesDesktopMediaQuery();
  const onDesktop = window.matchMedia(desktopMq).matches;
  const reducedMotion = window.matchMedia(
    resolveReducedMotionMediaQuery(),
  ).matches;

  window.scrollTo({
    top,
    behavior: onDesktop && !reducedMotion ? "smooth" : "auto",
  });
};

const YT_PAUSE_MESSAGE = '{"event":"command","func":"pauseVideo","args":""}';

const pauseExternalVideoIframe = (iframe) => {
  if (!iframe) return;

  if (iframe.classList?.contains("js-youtube")) {
    iframe.contentWindow?.postMessage(YT_PAUSE_MESSAGE, "*");

    return;
  }

  if (iframe.classList?.contains("js-vimeo")) {
    iframe.contentWindow?.postMessage('{"method":"pause"}', "*");
  }
};

if (!customElements.get("scrolling-content-tiles")) {
  class ScrollingContentTiles extends HTMLElement {
    constructor() {
      super();
      this.activeIndex = -1;
      this.scrollTriggerInstance = null;
      this.slideTween = null;
      this.contentTimeline = null;
      this.boundSectionLoad = null;
      this.boundSectionUnload = null;
      this.boundBlockSelect = null;
      this.boundPaginationClick = null;
      this.mediaQuery = null;
      this.boundViewport = null;
    }

    connectedCallback() {
      this.mediaQuery = window.matchMedia(
        resolveScrollingTilesDesktopMediaQuery(),
      );
      this.boundViewport = () => this.handleViewportChange();
      this.mediaQuery.addEventListener("change", this.boundViewport);
      this.boundSectionLoad = this.onSectionLoad.bind(this);
      this.boundSectionUnload = this.onSectionUnload.bind(this);

      document.addEventListener("shopify:section:load", this.boundSectionLoad);
      document.addEventListener(
        "shopify:section:unload",
        this.boundSectionUnload,
      );

      if (window.Shopify && window.Shopify.designMode) {
        this.boundBlockSelect = this.onBlockSelect.bind(this);

        document.addEventListener(
          "shopify:block:select",
          this.boundBlockSelect,
        );
      }

      this.boundPaginationClick = this.handlePaginationClick.bind(this);
      this.addEventListener("click", this.boundPaginationClick);

      scrollingTilesAttachWindowResize();
      this.scheduleInit();
    }

    disconnectedCallback() {
      if (this.mediaQuery && this.boundViewport) {
        this.mediaQuery.removeEventListener("change", this.boundViewport);
      }

      document.removeEventListener(
        "shopify:section:load",
        this.boundSectionLoad,
      );
      document.removeEventListener(
        "shopify:section:unload",
        this.boundSectionUnload,
      );

      if (this.boundBlockSelect) {
        document.removeEventListener(
          "shopify:block:select",
          this.boundBlockSelect,
        );
      }

      if (this.boundPaginationClick) {
        this.removeEventListener("click", this.boundPaginationClick);
        this.boundPaginationClick = null;
      }

      scrollingTilesDetachWindowResize();
      this.teardown();
    }

    scheduleInit() {
      const tryInit = () => {
        if (scrollingTilesGsapReady()) {
          this.initIfEligible();
          return;
        }

        window.requestAnimationFrame(() => {
          if (scrollingTilesGsapReady()) {
            this.initIfEligible();
            return;
          }

          window.addEventListener("load", tryInit, { once: true });
        });
      };

      tryInit();
    }

    onSectionLoad(event) {
      const sectionEl = this.closest(".shopify-section");

      if (!sectionEl || event.target !== sectionEl) return;

      this.teardown();

      window.requestAnimationFrame(() => this.initIfEligible());
    }

    onSectionUnload(event) {
      if (!event.target || !event.target.contains(this)) return;

      this.teardown();
    }

    onBlockSelect(event) {
      const sectionEl = this.closest(".shopify-section");

      if (!sectionEl || !event.target || !sectionEl.contains(event.target))
        return;

      const panel = event.target.closest(
        "[data-scrolling-tiles-content-panel]",
      );

      if (!panel || !this.contains(panel)) return;

      const idx = Number(panel.getAttribute("data-slide-index") || 0);

      this.scrollToSlideIndex(idx);
    }

    handlePaginationClick(event) {
      const item = event.target.closest(
        "[data-scrolling-tiles-pagination-item]",
      );

      if (!item || !this.contains(item)) return;

      event.preventDefault();

      const idx = Number(item.getAttribute("data-slide-index") || 0);

      if (!Number.isFinite(idx)) return;

      this.scrollToSlideIndex(idx);
    }

    scrollToSlideIndex(idx) {
      const slideCount = this.getSlideCount();

      if (slideCount < 2) return;

      if (idx < 0 || idx >= slideCount) return;

      const { mediaPanels, contentPanels, paginationRoot } =
        getScrollingSlideRoots(this);

      if (!this.scrollTriggerInstance || !scrollingTilesGsapReady()) {
        const prev = this.activeIndex < 0 ? 0 : this.activeIndex;

        if (idx === prev) return;

        this.playSlideTransition(
          slideCount,
          idx,
          prev,
          mediaPanels,
          contentPanels,
          paginationRoot,
        );

        this.activeIndex = idx;

        return;
      }

      const st = this.scrollTriggerInstance;
      const t = idx / (slideCount - 1);
      const targetScroll = st.start + t * (st.end - st.start);

      scrollDocumentToY(targetScroll);
    }

    handleViewportChange() {
      this.teardown();
      this.scheduleInit();
    }

    getSlideCount() {
      return Number(this.dataset.slideCount || 0);
    }

    /** True when the desktop media panel actually contains video / embed (source of truth vs Liquid). */
    activeMediaPanelHasPlayableVideo(idx) {
      const panel = this.querySelector(
        `[data-scrolling-tiles-media-panel][data-slide-index="${idx}"]`,
      );

      if (!panel) return false;

      return Boolean(
        panel.querySelector("video, iframe.js-youtube, iframe.js-vimeo"),
      );
    }

    /** Pause HTML5 / embed playback on every panel except `keepIdx` (scroll index changes). */
    pauseSlideMediaExcept(keepIdx) {
      const panels = this.querySelectorAll(
        "[data-scrolling-tiles-media-panel]",
      );

      panels.forEach((panel, i) => {
        if (i === keepIdx) return;

        const video = panel.querySelector("video");

        if (video) video.pause();

        panel
          .querySelectorAll("iframe.js-youtube, iframe.js-vimeo")
          .forEach(pauseExternalVideoIframe);
      });
    }

    /** After a slide settles: only the active slide may play; others stay paused. */
    syncActiveSlideMediaPlayback(activeIdx) {
      const panels = this.querySelectorAll(
        "[data-scrolling-tiles-media-panel]",
      );

      panels.forEach((panel, i) => {
        const video = panel.querySelector("video");
        const iframes = panel.querySelectorAll(
          "iframe.js-youtube, iframe.js-vimeo",
        );

        if (i !== activeIdx) {
          if (video) video.pause();
          iframes.forEach(pauseExternalVideoIframe);

          return;
        }

        if (video && video.hasAttribute("autoplay")) {
          const p = video.play();

          if (p && typeof p.catch === "function") {
            p.catch(() => {});
          }
        }
      });
    }

    getHeaderOffsetPx() {
      const raw = window
        .getComputedStyle(this)
        .getPropertyValue("--scrolling-tiles-header-offset")
        .trim();
      const n = parseFloat(raw);

      return Number.isFinite(n) ? n : SCROLLING_TILES.HEADER_OFFSET_FALLBACK_PX;
    }

    /** Viewport height available below the fixed header (pin + scroll math). */
    getScrollViewportHeightPx() {
      return Math.max(0, window.innerHeight - this.getHeaderOffsetPx());
    }

    initIfEligible() {
      if (!this.mediaQuery.matches) {
        this.teardown();
        return;
      }

      const slideCount = this.getSlideCount();
      const { mediaPanels, contentPanels, paginationRoot } =
        getScrollingSlideRoots(this);

      if (window.matchMedia(resolveReducedMotionMediaQuery()).matches) {
        this.teardown();
        this.initDesktopState(mediaPanels, contentPanels, paginationRoot, 0);
        this.settleMediaPanels(mediaPanels, 0);
        this.activeIndex = 0;
        return;
      }
      if (slideCount < 2) {
        this.teardown();
        this.initDesktopState(mediaPanels, contentPanels, paginationRoot, 0);
        this.settleMediaPanels(mediaPanels, 0);
        this.activeIndex = 0;
        return;
      }
      if (!scrollingTilesGsapReady()) {
        return;
      }

      gsap.registerPlugin(ScrollTrigger);
      this.setupScroll(slideCount, mediaPanels, contentPanels, paginationRoot);
    }

    initDesktopState(mediaPanels, contentPanels, paginationRoot, activeIdx) {
      if (typeof gsap === "undefined") return;

      this.settleMediaPanels(mediaPanels, activeIdx);

      contentPanels.forEach((el, i) => {
        const on = i === activeIdx;
        const inner = el.querySelector(".section-dual-tiles__content-inner");

        gsap.set(el, {
          opacity: on ? 1 : 0,
          visibility: on ? "visible" : "hidden",
          pointerEvents: on ? "auto" : "none",
        });
        el.classList.toggle("is-active", on);
        el.setAttribute("aria-hidden", on ? "false" : "true");

        if (inner) {
          const innerTargets = inner.children.length
            ? [...inner.children]
            : [inner];

          if (on) {
            gsap.set(innerTargets, { opacity: 1, yPercent: 0 });
          } else {
            gsap.set(innerTargets, {
              opacity: 0,
              yPercent: SCROLLING_TILES.CONTENT_ENTER_Y_PERCENT,
            });
          }
        }
      });

      this.updatePagination(paginationRoot, activeIdx);
    }

    settleMediaPanels(panels, activeIdx) {
      if (typeof gsap === "undefined") return;

      const zBefore = SCROLLING_TILES.MEDIA_Z_SETTLE_BEFORE;
      const zActive = SCROLLING_TILES.MEDIA_Z_SETTLE_ACTIVE;

      panels.forEach((panel, i) => {
        if (i < activeIdx) {
          gsap.set(panel, { yPercent: 0, zIndex: zBefore });
        } else if (i === activeIdx) {
          gsap.set(panel, { yPercent: 0, zIndex: zActive });
        } else {
          gsap.set(panel, { yPercent: 100, zIndex: zBefore });
        }

        panel.classList.toggle("is-active", i === activeIdx);
        panel.setAttribute("aria-hidden", i === activeIdx ? "false" : "true");
      });

      this.syncActiveSlideMediaPlayback(activeIdx);
      this.syncVideoPlayPauseButton(activeIdx);
    }

    syncVideoPlayPauseButton(activeIdx) {
      const wrap = this.querySelector("[data-scrolling-tiles-video-controls]");
      /* Mobile stack renders video buttons first in DOM — must not use
         querySelector on the host (would sync the wrong control). */
      const btn = wrap?.querySelector("video-play-pause-button");

      const sectionEl = this.closest("[id^='shopify-section-']");

      if (!sectionEl || !wrap || !btn) return;

      const safeIdx = activeIdx >= 0 ? activeIdx : 0;
      const showControl = this.activeMediaPanelHasPlayableVideo(safeIdx);

      wrap.classList.toggle(
        "section-scrolling-content-tiles__video-controls--inactive",
        !showControl,
      );
      wrap.setAttribute("aria-hidden", showControl ? "false" : "true");

      if (!showControl) {
        btn.hidden = true;

        return;
      }

      const prefix = `#${CSS.escape(sectionEl.id)}`;
      const target = [
        `${prefix} [data-scrolling-tiles-media-panel][data-slide-index="${safeIdx}"] video`,
        `${prefix} [data-scrolling-tiles-media-panel][data-slide-index="${safeIdx}"] iframe.js-youtube`,
        `${prefix} [data-scrolling-tiles-media-panel][data-slide-index="${safeIdx}"] iframe.js-vimeo`,
      ].join(", ");

      btn.dataset.mediaTarget = target;

      if (typeof btn.rebindMedia === "function") {
        btn.rebindMedia();
      }

      btn.hidden = false;
    }

    updatePagination(paginationRoot, idx) {
      if (!paginationRoot) return;

      const items = paginationRoot.querySelectorAll(
        "[data-scrolling-tiles-pagination-item]",
      );

      if (!items.length) return;

      items.forEach((el, i) => {
        const on = i === idx;

        el.classList.toggle("is-active", on);
        el.setAttribute("aria-current", on ? "true" : "false");
      });
    }

    playSlideTransition(
      slideCount,
      newIdx,
      prevIdx,
      mediaPanels,
      contentPanels,
      paginationRoot,
    ) {
      if (newIdx === prevIdx || typeof gsap === "undefined") return;

      if (this.slideTween) this.slideTween.kill();
      if (this.contentTimeline) this.contentTimeline.kill();

      this.initDesktopState(
        mediaPanels,
        contentPanels,
        paginationRoot,
        prevIdx,
      );

      const oldC = contentPanels[prevIdx];
      const newC = contentPanels[newIdx];
      const oldP = mediaPanels[prevIdx];
      const newP = mediaPanels[newIdx];

      if (!oldC || !newC || !oldP || !newP) return;

      gsap.set(oldC, { pointerEvents: "none" });

      const fadeOutDuration = SCROLLING_TILES.TRANSITION_FADE_OUT_S;
      const fadeUpDuration = SCROLLING_TILES.CONTENT_FADE_UP_IN_S;
      const newInner = newC.querySelector(".section-dual-tiles__content-inner");
      const newInnerTargets = newInner
        ? newInner.children.length > 0
          ? [...newInner.children]
          : [newInner]
        : null;

      this.contentTimeline = gsap.timeline();
      this.contentTimeline.to(oldC, {
        opacity: 0,
        duration: fadeOutDuration,
        ease: SCROLLING_TILES.EASE_CONTENT_OUT,
        delay: SCROLLING_TILES.TRANSITION_FADE_OUT_DELAY_S,
        onComplete: () => {
          oldC.classList.remove("is-active");
          oldC.setAttribute("aria-hidden", "true");
          gsap.set(oldC, { visibility: "hidden" });
        },
      });
      this.contentTimeline.set(newC, { visibility: "visible", opacity: 1 });
      if (newInnerTargets && newInnerTargets.length > 0) {
        this.contentTimeline.call(() => {
          newC.classList.add("is-active");
          newC.setAttribute("aria-hidden", "false");
          gsap.set(newC, { pointerEvents: "auto" });
        });
        this.contentTimeline.fromTo(
          newInnerTargets,
          {
            opacity: 0,
            yPercent: SCROLLING_TILES.CONTENT_ENTER_Y_PERCENT,
          },
          {
            opacity: 1,
            yPercent: 0,
            duration: fadeUpDuration,
            ease: SCROLLING_TILES.EASE_CONTENT_UP_IN,
            delay: SCROLLING_TILES.CONTENT_FADE_DELAY_S,
            stagger:
              newInnerTargets.length > 1
                ? SCROLLING_TILES.CONTENT_STAGGER_S
                : 0,
          },
        );
      } else {
        this.contentTimeline.fromTo(
          newC,
          { opacity: 0 },
          {
            opacity: 1,
            duration: fadeUpDuration,
            ease: SCROLLING_TILES.EASE_CONTENT_UP_IN,
            onStart: () => {
              newC.classList.add("is-active");
              newC.setAttribute("aria-hidden", "false");
              gsap.set(newC, { pointerEvents: "auto" });
            },
          },
        );
      }

      const incomingZ = SCROLLING_TILES.MEDIA_Z_INCOMING;
      const outgoingZ = SCROLLING_TILES.MEDIA_Z_OUTGOING;
      const slideDuration = SCROLLING_TILES.TRANSITION_SLIDE_S;

      if (newIdx > prevIdx) {
        gsap.set(oldP, { zIndex: outgoingZ });
        gsap.set(newP, { yPercent: 100, zIndex: incomingZ });
        this.slideTween = gsap.to(newP, {
          yPercent: 0,
          duration: slideDuration,
          ease: SCROLLING_TILES.EASE_SLIDE,
          onComplete: () => {
            this.settleMediaPanels(mediaPanels, newIdx);
          },
        });
      } else {
        gsap.set(oldP, { zIndex: outgoingZ });
        gsap.set(newP, { yPercent: -100, zIndex: incomingZ });
        this.slideTween = gsap.to(newP, {
          yPercent: 0,
          duration: slideDuration,
          ease: SCROLLING_TILES.EASE_SLIDE,
          onComplete: () => {
            gsap.set(oldP, { yPercent: 100 });
            this.settleMediaPanels(mediaPanels, newIdx);
          },
        });
      }

      this.updatePagination(paginationRoot, newIdx);
    }

    setupScroll(slideCount, mediaPanels, contentPanels, paginationRoot) {
      this.teardown();

      const scrollContainer = this.querySelector(
        "[data-scrolling-tiles-scroll-trigger]",
      );
      const pinTarget = this.querySelector("[data-scrolling-tiles-pin-target]");

      if (!scrollContainer || !pinTarget) return;

      this.initDesktopState(mediaPanels, contentPanels, paginationRoot, 0);
      this.activeIndex = 0;

      const scrollPerSlidePx = () =>
        this.getScrollViewportHeightPx() * SCROLLING_TILES.SCROLL_PER_SLIDE_VH;

      const extraScrollAfterLastPx = () =>
        this.getScrollViewportHeightPx() *
        SCROLLING_TILES.SCROLL_EXTRA_AFTER_LAST_VH;

      this.scrollTriggerInstance = ScrollTrigger.create({
        trigger: scrollContainer,
        pin: pinTarget,
        start: () => `top ${this.getHeaderOffsetPx()}px`,
        end: () =>
          "+=" +
          ((slideCount - 1) * scrollPerSlidePx() + extraScrollAfterLastPx()),
        pinSpacing: true,
        anticipatePin: 0,
        pinType: "fixed",
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          const idx = Math.round(self.progress * (slideCount - 1));

          if (idx === this.activeIndex) return;

          const prev = this.activeIndex;

          this.pauseSlideMediaExcept(idx);

          this.activeIndex = idx;
          this.playSlideTransition(
            slideCount,
            idx,
            prev,
            mediaPanels,
            contentPanels,
            paginationRoot,
          );
        },
      });
    }

    teardown() {
      if (this.scrollTriggerInstance) {
        this.scrollTriggerInstance.kill();
        this.scrollTriggerInstance = null;
      }
      if (this.slideTween) {
        this.slideTween.kill();
        this.slideTween = null;
      }
      if (this.contentTimeline) {
        this.contentTimeline.kill();
        this.contentTimeline = null;
      }

      this.activeIndex = -1;
    }
  }

  customElements.define("scrolling-content-tiles", ScrollingContentTiles);
}
